import React, { useEffect, useState } from "react";
import "./InsightList.scss";
import { Link, LinkProps } from "polyrouter";
import {
  Category,
  Indicator,
  LocationScope,
  TimeScope
} from "generated/graphql";
import i18next, { useTranslation } from "utilities/i18n";
import VisibilitySensor from "react-visibility-sensor";
import { UseDeviceAction } from "routes/Manage/Actions/useDeviceAction";
import {
  differenceInCalendarMonths,
  differenceInCalendarQuarters,
  differenceInCalendarWeeks,
  differenceInCalendarYears,
  getDay,
  parse
} from "date-fns";
import { dateToString } from "utilities/datetime";
import {
  priorityInsightsListReturnType,
  UsePriorityInsight
} from "routes/Insights/Priority/usePriorityInsight";
import { NoResultsIndicatorAnimation, PremiumBetaBadge } from "elements";
import { truncDecimals } from "utilities/numberUtils";
import { getDataGroupByParam } from "routes/Insights/Priority/PriorityInsights";
import { useDispatch } from "react-redux";
import { changeInsightsCursor } from "store/paging";
import { generatePath, useHistory } from "react-router";
import { trackInterest } from "monitoring";
import { getDayOfWeek } from "Constants";

export interface GenericInsight {
  title: string;
  details: string;
  link: LinkProps["to"];
  linkTitle: string;
}

export interface DeviceAction extends UseDeviceAction, GenericInsight {}

export interface InsightListProps {
  insightList?: priorityInsightsListReturnType | null;
  actionsList?: Array<DeviceAction> | null;
  endCursor?: string | null;
}

export const findRelativeTimePeriod = (
  date: string | null | undefined, // string in yyyy-MM-dd
  period: TimeScope | undefined
) => {
  const periodStr = i18next.t(period || "");
  if (!date) {
    if (period?.toString() === "DAY") {
      return i18next.t("TODAY");
    }
    if (period?.toString() === "CURRENT") {
      return i18next.t("CURRENT");
    }
    if (period) {
      return i18next.t("THIS_PERIOD_EMPTY", { period: periodStr });
    }
    return periodStr;
  }

  const today = new Date();
  date = date.split("T")[0];
  const day = parse(date, "yyyy-MM-dd", new Date());
  const dayStr = dateToString(day);
  let difference = 0;
  switch (period) {
    case undefined:
      return i18next.t("DAY");
    case TimeScope.Day:
      const dayKey = getDayOfWeek(getDay(day));
      return (
        i18next.t("DAY_OF_X", {
          day: dayStr,
          weekday: i18next.t(dayKey)
        }) || ""
      );
    case TimeScope.Week:
      switch (differenceInCalendarWeeks(today, day)) {
        case 1:
          return i18next.t("LAST_PERIOD", {
            period: periodStr,
            start: dayStr
          });
        case 0:
          return i18next.t("THIS_PERIOD", {
            period: periodStr,
            start: dayStr
          });
        default:
          return i18next.t("PERIOD_OF_X", {
            period: periodStr,
            start: dayStr
          });
      }
    case TimeScope.Month:
      difference = differenceInCalendarMonths(today, day);
      break;
    case TimeScope.Quarter:
      difference = differenceInCalendarQuarters(today, day);
      break;
    case TimeScope.Year:
      difference = differenceInCalendarYears(today, day);
      break;
    default:
      difference = 0;
      break;
  }
  switch (difference) {
    case 1:
      return i18next.t("LAST_PERIOD_EMPTY", {
        period: periodStr
      });
    case 0:
      return i18next.t("THIS_PERIOD_EMPTY", {
        period: periodStr
      });
    default:
      return i18next.t("PERIOD_OF_X", {
        period: periodStr,
        start: dayStr
      });
  }
};

export const indyTrending = (
  prev: Indicator | null | undefined,
  curr: Indicator | null | undefined
): number | undefined => {
  const i1 = truncIndicator(prev);
  const i2 = truncIndicator(curr);
  if (i1 === null || i2 === null) {
    return undefined;
  }
  if (i1 === i2) {
    return 0;
  }
  return i2 > i1 ? 1 : -1;
};

// Converts indicator value from a decimal to whole number if
// a percentage, otherwise rounds the value to whole number.
export const truncIndicator = (
  indy: Indicator | undefined | null
): number | null => {
  if (!indy) {
    return null;
  }
  return indy.__typename === "CountOfTotalIndicator"
    ? truncDecimals(indy.percentage * 100)
    : truncDecimals(indy.average);
};

// TODO: What is a "tMetric"?
export const tMetric = () => (
  indy: Indicator | undefined | null,
  forceCount?: boolean
): string => {
  if (!indy) {
    return "";
  }

  if (forceCount && indy.__typename !== "AverageIndicator") {
    return truncDecimals(indy.count).toString();
  }

  if (indy.__typename === "CountOfTotalIndicator") {
    return i18next.t("PERCENTAGE_INDICATOR", {
      percentage: truncIndicator(indy)
    });
  }

  return i18next.t("AVERAGE_INDICATOR", {
    average: truncIndicator(indy)?.toLocaleString()
  });
};

const DeviceActionCard: React.FC<DeviceAction> = ({
  action,
  model,
  count,
  title,
  locations,
  details,
  linkTitle
}) => {
  const { t } = useTranslation();
  let more = "";
  if (locations.length > 3) {
    more = t("MORE_COUNT", { count: locations.length - 3 });
  }
  return (
    <div
      className="insight-card p-15 hover-shadow"
      data-testid="device-actions-test"
    >
      <div className="insight-header d-flex">
        <div className="metric mr-5">{count}</div>
        <div className="insight">{title}</div>
      </div>
      {locations.length ? (
        <div className="insight-card-text location mt-5">
          <ul className="tags pl-0 d-flex">
            <li>
              <div className="scope-tag more pl-0">Sites:</div>
            </li>
            {locations.slice(0, 3).map((location, i) => (
              <li key={location ?? i}>
                <div className="scope-tag mr-5">{location}</div>
              </li>
            ))}
            {more && (
              <li>
                <div className="scope-tag more">{more}</div>
              </li>
            )}
          </ul>
        </div>
      ) : null}
      {details && (
        <div className="insight-card-text description mt-15">{details}</div>
      )}
    </div>
  );
};

interface InsightDetailCardProps {
  endCursor?: string | null;
  hasNextPage: boolean;
}

const InsightDetailCard: React.FC<UsePriorityInsight &
  InsightDetailCardProps> = ({
  id,
  metric,
  category,
  title,
  model,
  version,
  location,
  locationScope,
  details,
  linkTitle,
  trending,
  cursor,
  endCursor,
  hasNextPage,
  site,
  timePeriodStart,
  groupBy,
  period
}) => {
  const { t } = useTranslation();
  const tM = tMetric();
  const dispatch = useDispatch();

  const [visible, setVisible] = useState(false);

  const onChange = (isVisible: boolean) => {
    if (isVisible) {
      setVisible(isVisible);
      if (cursor === endCursor && hasNextPage) {
        dispatch(changeInsightsCursor(cursor));
      }
    }
  };

  return (
    <VisibilitySensor
      offset={{ bottom: -1000 }}
      active={!visible}
      onChange={onChange}
    >
      <div
        className={`insight-card hover-shadow restore-${id}`}
        data-testid="insight-details-test"
      >
        <div className="insight-card-text period mt-n1 mr-n5">
          {findRelativeTimePeriod(timePeriodStart || "", period)}
        </div>
        <div className="insight-header d-flex mt-5">
          <div className="metric mr-5">{tM(metric)}</div>
          <div className="insight">{title}</div>
        </div>
        <div className="d-flex mt-5">
          {locationScope === LocationScope.ModelVersion ? (
            <>
              <div className="scope-tag mr-5">
                {t("DEVICE_MODEL_WITH_NAME", { model })}
              </div>
              <div className="scope-tag">{`${t("VERSION")}: ${version}`}</div>
            </>
          ) : (
            <div className="scope-tag">{`${t(locationScope)}: ${groupBy}`}</div>
          )}
        </div>
        <div className="insight-card-text description mt-15">{details}</div>
        {category === Category.Room && <PremiumBetaBadge />}
      </div>
    </VisibilitySensor>
  );
};

export const InsightList: React.FC<InsightListProps> = ({
  insightList = null,
  actionsList = null
}: InsightListProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  useEffect(() => {
    const item = document.querySelector(".restore-" + history?.location.state);
    if (item) {
      item.scrollIntoView();
    }
  });

  if (!actionsList && (!insightList || insightList.insights.length === 0)) {
    return <NoResultsIndicatorAnimation title={t("NO_INSIGHTS_AVAILABLE")} />;
  }

  if (!insightList && actionsList?.length === 0) {
    return <NoResultsIndicatorAnimation title={t("NO_ACTIONS_AVAILABLE")} />;
  }

  const endCursor = insightList?.pageInfo.endCursor;
  const hasNextPage = insightList?.pageInfo.hasNextPage;

  return (
    <ul className="insight-list p-0">
      {insightList?.insights.map((field, idx) => {
        return (
          <li className="mb-2" key={field.id + ":" + idx}>
            <Link
              persist={["InsightsFilterState"]}
              to={generatePath("/insights/details/:id", { id: field.id })}
              onClick={trackInterest(
                field.title,
                "PriorityInsights",
                field.locationScope,
                field.period
              )}
            >
              <InsightDetailCard
                {...field}
                endCursor={endCursor}
                hasNextPage={!!hasNextPage}
                groupBy={getDataGroupByParam(field)}
              />
            </Link>
          </li>
        );
      })}
      {actionsList?.map((field, idx) => (
        <li
          className="mb-2"
          key={`${field.model}.${field.action}.${field.version}:${idx}`}
        >
          <Link
            to={field.link}
            data-testid="DeviceActionLink"
            onClick={trackInterest(field.action.toLowerCase(), "DeviceActions")}
          >
            <DeviceActionCard {...field} />
          </Link>
        </li>
      ))}
    </ul>
  );
};
